// Layout PURO do "stage" do <video> fixo (handoff sem re-parent, ramo native-ts).
//
// No ramo native-ts (webOS punch-through) o `appendChild` do <video> entre o Hero
// e o PlayerScreen ESVAZIA o elemento nativo (readyState 0 → reload ~2s). O fix é
// NÃO re-parentar: o <video> mora fixo num portal (o "stage") e é REPOSICIONADO
// por CSS pro slot do Hero OU do fullscreen. Provado no device: reposição por CSS
// (position:fixed + top/left/width/height) NÃO esvazia (rs continua 4, currentTime
// avança) e o plano de HW segue a geometria de LAYOUT.
//
// Este módulo é a decisão PURA de estilo (rect+modo → estilo do stage / object-fit
// do vídeo). Sem DOM, sem side-effect — testável e importável por puppeteer.

import type { AttachMode } from './types'

/** Retângulo do slot (subconjunto de DOMRect que consumimos — left/top/width/
 *  height existem no Chr38+, ao contrário de x/y). */
export interface SlotRect {
  left: number
  top: number
  width: number
  height: number
}

/**
 * z-index do stage no ramo native-ts. POSITIVO e BAIXO de propósito:
 *  - ACIMA do fundo das telas base (Hero `.container` #000 e PlayerScreen
 *    `.container` black, ambos z-auto) → o vídeo pinta SOBRE o preto, nunca some.
 *  - ABAIXO de TODOS os overlays do player (statusOverlay z5, PlayerOverlay z6,
 *    SourceSwitcher z50, AutoSwitchToast z60, TrialOverlay z-máx) → eles cobrem o
 *    vídeo naturalmente, sem bump. Os overlays do Hero (placeholder/audioOverlay)
 *    sobem pra z3 no CSS (só `html[data-native-ts]`) por serem z-auto.
 * `#root` é estático (não cria stacking context no caso comum), então este número
 * compara direto com os z-index das telas no stacking raiz. Sob a tática B de
 * escala (`isCanvasScaled`) o #root vira contexto — por isso o gate do fix EXCLUI
 * esse caso e cai no appendChild (ver LivePlayerProvider).
 */
export const STAGE_Z_INDEX = 2

/** object-fit por modo: fullscreen respeita o AR do stream (contain, letterbox
 *  preto do próprio <video>); Hero preenche o slot (cover, cortando bordas). */
export function objectFitForMode(mode: AttachMode): 'cover' | 'contain' {
  return mode === 'fullscreen' ? 'contain' : 'cover'
}

/** Estilo do stage VISÍVEL, ancorado no rect do slot (coords de viewport, pois é
 *  position:fixed e — no gate do fix — não há transform no #root). Arredonda pra
 *  px inteiro (subpixel no plano de HW pode borrar a borda do recorte). */
export function shownStageStyle(rect: SlotRect): Record<string, string> {
  return {
    position: 'fixed',
    left: `${Math.round(rect.left)}px`,
    top: `${Math.round(rect.top)}px`,
    width: `${Math.round(rect.width)}px`,
    height: `${Math.round(rect.height)}px`,
    opacity: '1',
    zIndex: String(STAGE_Z_INDEX),
    overflow: 'visible',
    pointerEvents: 'none',
  }
}

// ===== Gatilho de RE-ANCORAGEM do stage (o slot do Hero SE MOVE) =====
// O slot do Hero desce/sobe (~106px, medido no device) quando a TopNav
// colapsa/expande. A TopNav decide isso pela ZONA de foco: colapsa quando o foco
// NÃO está nela, expande quando está (`data-collapsed={!navHasFocus}`,
// TopNav.tsx). O invariante do movimento do slot é, portanto, o CRUZAMENTO dessa
// zona — não o tipo de tecla. Ancorar no tipo de tecla (ex.: "só setas
// verticais") tem furo: o BACK hierárquico faz `setFocus('nav-live')` sem seta
// nem resize, e o stage ficaria preso, descolado do Hero. Espelhamos a MESMA
// condição da TopNav (`focusKey.startsWith('nav-')`), então qualquer caminho que
// mova o foco pra dentro/fora da nav (seta, BACK, setFocus programático) dispara
// a re-medição — e o surfe horizontal (chip→chip, card→card), que NÃO cruza a
// zona, não dispara (preserva o ganho de perf: sem getBoundingClientRect por
// frame no surfe).

/** A focusKey pertence à zona da TopNav? Mesma regra do `navHasFocus` da TopNav
 *  (todos os itens do menu usam o prefixo `nav-`). String vazia (sem foco) = fora. */
export function isNavFocusKey(focusKey: string | null | undefined): boolean {
  return typeof focusKey === 'string' && focusKey.startsWith('nav-')
}

/**
 * O foco CRUZOU a zona nav↔conteúdo entre `prevKey` e `nextKey`? True só quando
 * a pertença à nav MUDA (entrou OU saiu) — é o exato momento em que a TopNav
 * colapsa/expande e o slot do Hero se move. Surfe dentro da mesma zona
 * (conteúdo→conteúdo ou nav→nav) retorna false. É a decisão pura que o poll de
 * foco do provider usa pra armar o burst de re-ancoragem.
 */
export function crossedNavZone(
  prevKey: string | null | undefined,
  nextKey: string | null | undefined,
): boolean {
  return isNavFocusKey(prevKey) !== isNavFocusKey(nextKey)
}

// ===== Quando o burst de re-ancoragem PARA (política pura) =====
// O corte anterior era um PRAZO FIXO de 500ms contado a partir do cruzamento de
// zona. Esse número foi calibrado contra a única coisa que se tinha medido — a
// transição CSS da TopNav (`--transition-base`, 0ms em vlow/low até 250ms em
// high). Só que o slot não se move quando a transição começa: ele se move quando
// o React RE-RENDERIZA a TopNav com `data-collapsed` novo. E essa latência a
// própria casa já mediu, na LG do dono, em OUTRO lugar do código: "no tier low um
// unico movimento de foco custa ate 1,7 s" (TopNav.tsx, bloco do dwell). Ou seja:
// o prazo de 500ms podia vencer INTEIRO antes de o layout encostar no primeiro
// pixel, e aí o stage congelava na geometria da nav EXPANDIDA enquanto a tela já
// era a da nav COLAPSADA — o preview cobrindo a faixa de chips (medido por CDP na
// LG: stage em top=122 com os chips em top=497; 122-16 = os ~106px da nav).
//
// Prazo é PROXY (tempo de relógio); o invariante é a GEOMETRIA ter parado de se
// mexer. Então a política passa a ser:
//   - enquanto esperamos um movimento que ainda não aconteceu, continue;
//   - depois que ele aconteceu, continue até o silêncio geométrico;
//   - e um TETO absoluto, porque rAF perpétuo custa orçamento de scroll.
// O teto NÃO é a garantia de convergência — quem garante é o sentinela de
// baixa frequência do provider, que só existe porque este teto pode vencer antes
// de um aparelho lento assentar. Ver `startStageBurst` / sentinela.

/** Silêncio geométrico (sem mudança de chave) que encerra o burst. */
export const STAGE_BURST_QUIET_MS = 200

/**
 * Teto absoluto de um burst. Nunca rAF perpétuo.
 *
 * ⚠️ ESTE NÚMERO TEM QUE FICAR ACIMA DO PIOR CASO DE RENDER MEDIDO NESTA CASA
 * (~1,7 s pra um movimento de foco assentar no tier low, ver TopNav.tsx). O
 * primeiro corte usou 1500 — ABAIXO desse 1,7 s — e o review mediu a
 * consequência com braço de controle:
 *     layout assentando a 1700ms → 14 frames (~224ms) cobrindo a faixa INTEIRA
 *     layout assentando a 1400ms → 0 frames
 * O controle isola a variável: a janela existia PORQUE 1500 < 1700. Com 2000 ela
 * fecha (converge no frame 1).
 *
 * O teto NÃO é a garantia de convergência — é a garantia de que a convergência é
 * IMEDIATA no caso conhecido. Se um aparelho passar de 2000ms, quem corrige é o
 * sentinela, e aí a exposição é de até UM período dele (`STAGE_SENTINEL_MS`), não
 * "para sempre". Essa é a promessa honesta; "nunca cobre" seria falso.
 */
export const STAGE_BURST_CEILING_MS = 2000

/**
 * Período do sentinela de baixa frequência (reposiciona quando não há burst).
 *
 * É o TETO DA EXPOSIÇÃO quando o layout assenta depois do burst: o preview fica
 * stale por no máximo um período destes. Encurtar reduz a exposição e aumenta o
 * custo ocioso (medido: 2,50 `getBoundingClientRect`/s com o preview visível,
 * 0/s escondido).
 */
export const STAGE_SENTINEL_MS = 400

/** Estado do burst no frame corrente, já reduzido a números puros. */
export interface BurstTick {
  /** ms desde o início (ou o último rearme) deste burst. */
  elapsedMs: number
  /** ms desde a última vez que a chave de geometria MUDOU. */
  msSinceLastChange: number
  /** a geometria já mudou pelo menos uma vez neste burst? */
  sawMovement: boolean
  /** este burst foi armado por um gatilho que PROMETE movimento (cruzamento de
   *  zona)? Se sim, não desista antes de vê-lo — é o caso do aparelho lento. */
  expectMovement: boolean
}

/** O burst continua no próximo frame? Decisão PURA — sem DOM, sem relógio. */
export function burstShouldContinue(t: BurstTick): boolean {
  if (t.elapsedMs >= STAGE_BURST_CEILING_MS) return false
  if (t.expectMovement && !t.sawMovement) return true
  return t.msSinceLastChange < STAGE_BURST_QUIET_MS
}

/** Estilo do stage ESCONDIDO (1x1 fora da tela, atrás de tudo). Usado ao
 *  suspender (sair da live) e destruir — evita um retângulo preto do <video>
 *  vazar por cima de outra tela. NÃO usado no handoff Hero↔fullscreen (lá o
 *  stage segue visível e só reposiciona, pra não piscar preto). */
export function hiddenStageStyle(): Record<string, string> {
  return {
    position: 'fixed',
    left: '-99999px',
    top: '0px',
    width: '1px',
    height: '1px',
    opacity: '0',
    zIndex: '-1',
    overflow: 'hidden',
    pointerEvents: 'none',
  }
}
